import { useAuth0 } from "@auth0/auth0-react";
import { Button, Dropdown, Table , Input, Space } from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import AddResellerModal from "../../modals/AddResellerModal";
import UserResetPasswordModal from "../../modals/UserResetPasswordModal";
import Search from "../../shared/Search";
import { addReseller , updateResellerStatus, getResellers } from "../../utils/users";
import { useNavigate } from "react-router-dom";

import { SearchOutlined } from "@ant-design/icons";


const getColumnSearchProps = (dataIndex, placeholder = "Search") => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={`${placeholder}...`}
        value={selectedKeys[0]}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: "block" }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
  ),
  onFilter: (value, record) =>
    record[dataIndex]
      ?.toString()
      .toLowerCase()
      .includes(value.toLowerCase()),
});


const Resellers = () => {
  const navigate = useNavigate();
  const { isLoading, getIdTokenClaims } = useAuth0();
  const [resellers, setResellers] = useState([]);
  const [showUserResetPasswordModal, setShowUserResetPasswordModal] =
    useState();
  const [userResetPasswordSelected, setUserResetPasswordSelected] = useState();

  const [token, setToken] = useState();
  const [fetching, setFetching] = useState(false);
  const [query, setQuery] = useState("");

  const [addUserModalOpen, setAddResellerModalOpen] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      fetchResellers();
    }
  }, [isLoading, query]);

  const fetchResellers = async () => {
    try {
      setFetching(true);
      setResellers([]);

      const token = await getIdTokenClaims();

      setToken(token.__raw);

      getResellers(token.__raw, query)
        .then((res) => {
          if (res.data.success === false) {
            alert(res.data.message);
          } else {
            setResellers(res.data.resellers);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setFetching(false);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const handleStatusUpdateClicked = (id,status) => {
    if (window.confirm("Are you sure you want to update user status?")) {
      setFetching(true);

      updateResellerStatus(token, id, status)
        .then((res) => {
          if (res.data.success === false) {
            alert(res.data.message);
          } else {
            alert(res.data.message);
            fetchResellers();
          }
        })
        .catch((err) => {
          alert("Something went wrong. Please contact support.");
          console.error(err);
        });
    }
  };

  const handleCustomersButtonClick = (id) =>  {
    navigate(`/resellers/users/${id}`);
  };

  const handleSearchChange = useCallback(
    debounce((text) => {
      setQuery(text);
    }, 500),
    []
  );

  const handleAddResellerModalClose = () => {
    setAddResellerModalOpen(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      render: (_, { full_name }) => (
        <div className={"flex gap-2 items-center text-gray-800"}>{full_name}</div>
      ),
      sorter: (a, b) => a.full_name.localeCompare(b.full_name),
      ...getColumnSearchProps("full_name", "Search name"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      ...getColumnSearchProps("email", "Search email"),
    },
    {
      title: "Referral Code",
      dataIndex: "referral_code",
      key: "referral_code",
      sorter: (a, b) => a.referral_code.localeCompare(b.referral_code),
      ...getColumnSearchProps("referral_code", "Search code"),
    },
    {
      title: "Referral Commissions",
      dataIndex: "referral_fee_percent",
      key: "referral_fee_percent",
      sorter: (a, b) => a.referral_code.localeCompare(b.referral_code),
      ...getColumnSearchProps("referral_fee_percent", "Search Commissions"),
    },
    {
      title: "Total Referrals",
      dataIndex: "referral_count",
      key: "referral_count",
      sorter: (a, b) => a.referral_count.localeCompare(b.referral_count),
      render: (_, record) => (
        <span
          className="text-blue-600 cursor-pointer hover:underline"
          onClick={() => handleCustomersButtonClick(record.id)}
        >
          {record.referral_count}
        </span>
      )
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "date",
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "30%",
      sorter: (a, b) => a.status.localeCompare(b.status),
      filters: [
        {
          text: "Active",
          value: 1,
        },
        {
          text: "Inactive",
          value: 0,
        },
      ],
      onFilter: (value, record) => record.status == value,

      render: (_, { id, role , status }) => {
        return (
          <div className={"text-gray-500 flex justify-between items-center"}>
            <div
              className={`flex w-max rounded-lg p-1 px-2 gap-2 items-center`}
            >
              {status == 1?'Active':'Inactive'}
            </div>

            <Dropdown
              placement="bottomRight"
              trigger={["click"]}
              dropdownRender={(menu) => (
                <div
                  className={
                    "bg-white w-36 p-2 border border-gray-100 rounded-lg shadow-lg"
                  }
                >
                  <div
                    className={
                      "font-medium flex items-center gap-2  text-gray-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    }
                    onClick={() => {
                      handleStatusUpdateClicked(id,status==1?'inactive':'active');
                    }}
                  >
                    <ReactSVG src={"/assets/icons/edit.svg"} />
                    {status==1?'inactive':'active'}
                  </div>
                  <div
                    className="font-medium flex items-center gap-2 text-gray-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    onClick={() => handleCustomersButtonClick(id)}
                  >
                    <ReactSVG src="/assets/icons/user.svg" />
                    Customers
                  </div>
                </div>
              )}
            >
              <ReactSVG
                className={"p-1 cursor-pointer rounded-lg hover:bg-white"}
                src={"/assets/icons/dot-menu.svg"}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const statusHighlight = {
    active: "text-slate-600 bg-slate-50",
    inactive: "text-green-600 bg-green-50",
  };

  const mobileRender = resellers.map((item, index) => {
    return (
      <div
        key={index}
        className={
          "flex justify-between border rounded-lg border-gray-300 text-sm py-3 mt-3 px-4 text-gray-500"
        }
      >
        {/*<div>*/}
        {/*    image goes here*/}
        {/*</div>*/}
        <div>
          <p className={"mb-2 font-medium text-gray-600"}>{item.full_name}</p>
          <p className={"mb-2"}>{item.email}</p>
          <div
            className={`flex w-max rounded-lg p-1 px-2 gap-2 items-center`}
          >
            {item.status==1?'active':'inactive'}
          </div>
        </div>
        <div className={"flex items-end flex-col justify-between"}>
          <div>{item.created_at}</div>
          <Dropdown
            placement="bottomRight"
            trigger={["click"]}
            dropdownRender={(menu) => (
              <div
                className={
                  "bg-white w-36 p-2 border border-gray-100 rounded-lg shadow-lg"
                }
              >
                
                <div
                  className={
                    "font-medium flex items-center gap-2  text-gray-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                  }
                  onClick={() => {
                    handleStatusUpdateClicked(item.id,item.status==1?'inactive':'active');
                  }}
                >
                  <ReactSVG src={"/assets/icons/edit.svg"} />
                  {item.status==1?'inactive':'active'}
                </div>
                <div
                  className="font-medium flex items-center gap-2 text-gray-600 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                  onClick={() => handleCustomersButtonClick(item.id)}
                >
                  <ReactSVG src="/assets/icons/user.svg" />
                  Customers
                </div>
              </div>
            )}
          >
            <ReactSVG
              className={"p-1 cursor-pointer rounded-lg hover:bg-white"}
              src={"/assets/icons/dot-menu.svg"}
            />
          </Dropdown>
        </div>
      </div>
    );
  });

  return (
    <div className={"px-8"}>
      <AddResellerModal
        modalOpen={addUserModalOpen}
        onClose={handleAddResellerModalClose}
        onComplete={fetchResellers}
      />

      <UserResetPasswordModal
        showModal={showUserResetPasswordModal}
        id={userResetPasswordSelected}
        onClose={() => {
          setShowUserResetPasswordModal(false);
        }}
      />

      <div className={"flex items-center justify-between"}>
        <br></br>
        <br></br>
        <Button
          className={'px-2 text-sm'} onClick={() => setAddResellerModalOpen(true)}>
          Add New Reseller
         </Button>   
      </div>
      <div className={"mt-6 lg:block pb-3 mb-10 hidden"}>
        <Table
          dataSource={resellers}
          columns={columns}
          loading={fetching}
          pagination={{
            showSizeChanger: true, // Enables the dropdown for selecting rows per page
            pageSizeOptions: ["10", "20", "50", "100"], // Defines options for rows per page
            defaultPageSize: 20, // Default number of rows per page
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>
      <div className={"lg:hidden block"}>{mobileRender}</div>
    </div>
  );
};

export default Resellers;
